import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Drawer } from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import { useUser } from '../../context/UserContext';
import {
  HomeOutlined,
  DashboardOutlined,
  DollarOutlined,
  UserOutlined,
  MenuOutlined,
  LogoutOutlined,
  BarChartOutlined,
  SunOutlined,
  MoonOutlined,
  BankOutlined,
  PieChartOutlined,
  FundOutlined,
  StockOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './LandingLayoutRedesign.css';
import logo from '../../assets/logo1.png';
import apiClient from "../../services/apiClient";
import { clearAuthData, isExternalUser } from '../../utils/roleUtils';

const { Header, Content, Footer } = Layout;

const SHARED_LANDING_NAV_ITEMS = [
  {
    key: '/issuer-categorization',
    label: 'Issuers Categories',
    icon: <BankOutlined />,
  },
  {
    key: '/isins',
    label: 'Instruments',
    icon: <BankOutlined />,
  },
  {
    key: '/fixed-deposits',
    label: 'Fixed Deposits',
    icon: <FundOutlined />,
  },
  {
    key: '/market-insights',
    label: 'Market Insights',
    icon: <BarChartOutlined />,
  },
  {
    key: '/call-money',
    label: 'Call Money',
    icon: <FundOutlined />,
  },
  {
    key: '/risk-based-pricing',
    label: 'Risk Based Pricing',
    icon: <DollarOutlined />,
  },
  {
    key: '/construct-portfolio',
    label: 'Portfolio',
    icon: <PieChartOutlined />,
  },
  {
    key: '/import-investments',
    label: 'Import CAS',
    icon: <ImportOutlined />,
  },
];

const AUTH_ONLY_NAV_ITEMS = [
  {
    key: '/debt-trading',
    label: 'Debt Trading',
    icon: <StockOutlined />,
  },
];

const LandingLayoutRedesign = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const { userData, clearUserData } = useUser();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isAuthenticated = Boolean(userData?.id);
  const isExternalAuthenticated = isExternalUser(userData);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = useMemo(() => {
    const authItems = isAuthenticated ? AUTH_ONLY_NAV_ITEMS : [];

    if (isExternalAuthenticated) {
      return [
        {
          key: '/my-portfolio',
          label: 'My Portfolio',
          icon: <DashboardOutlined />,
        },
        ...SHARED_LANDING_NAV_ITEMS,
        ...authItems,
      ];
    }

    return [
      {
        key: '/home',
        label: 'Home',
        icon: <HomeOutlined />,
      },
      ...SHARED_LANDING_NAV_ITEMS,
      ...authItems,
    ];
  }, [isExternalAuthenticated, isAuthenticated]);

  const getActiveNavKey = useCallback(() => {
    const path = location.pathname;

    if (path === '/dashboard' || path === '/my-portfolio') return '/my-portfolio';
    if (path === '/profile') return '/my-portfolio';
    if (path === '/home' || path === '/') return '/home';
    if (path === '/bond-categories') return '/bond-categories';
    if (path === '/issuer-categorization') return '/issuer-categorization';
    if (path === '/market-insights') return '/market-insights';
    if (path === '/call-money') return '/call-money';
    if (path === '/risk-based-pricing') return '/risk-based-pricing';
    if (path.startsWith('/fixed-deposits')) return '/fixed-deposits';
    if (path === '/yield-all-data') return '/yield-all-data';
    if (path === '/announcements') return '/announcements';
    if (path === '/construct-portfolio' || path.startsWith('/construct-portfolio/')) {
      return '/construct-portfolio';
    }
    if (path === '/import-investments' || path.startsWith('/import-investments')) {
      return '/import-investments';
    }
    if (path === '/watchlist') return '/isins';
    if (path.startsWith('/isins') || path.startsWith('/isin-details')) return '/isins';
    if (path.startsWith('/issuers') || path.startsWith('/issuer-details')) return '/issuers';
    if (path === '/debt-trading') return '/debt-trading';
    if (path === '/pricing') return '/pricing';

    return isExternalAuthenticated ? '/my-portfolio' : '/home';
  }, [location.pathname, isExternalAuthenticated]);

  const userMenuItems = useMemo(() => [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ], []);

  const handleMenuClick = ({ key }) => {
    navigate(key);
    setMobileMenuVisible(false);
  };

  const handleLogout = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      await apiClient.post(`${baseUrl}/user/logout`);
    } catch (e) {
      // Proceed with client-side cleanup even if logout API fails
    } finally {
      clearAuthData(navigate, clearUserData);
      setMobileMenuVisible(false);
    }
  }, [navigate, clearUserData]);

  const handleUserMenuClick = ({ key }) => {
    if (key === 'profile') {
      navigate('/profile');
      return;
    }
    if (key === 'logout') {
      handleLogout();
    }
  };

  const getUserInitials = () => {
    const firstName = userData.firstName || '';
    const lastName = userData.lastName || '';
    const name = `${firstName}${lastName}`.trim();

    if (name) {
      return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase();
    }

    return userData.email ? userData.email.substring(0, 2).toUpperCase() : 'U';
  };

  const handleLogoClick = () => {
    navigate(isExternalAuthenticated ? '/my-portfolio' : '/home');
  };

  return (
    <Layout className={`landing-layout ${isDark ? "dark" : ""}`} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header className={`landing-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <div className="header-left">
            <div className="landing-header-logo" onClick={handleLogoClick}>
              <img src={logo} alt="SimplyBond" className="landing-header-logo__image" />
            </div>
            <div className="header-nav">
              <Menu
                mode="horizontal"
                selectedKeys={[getActiveNavKey()]}
                items={navItems}
                onClick={handleMenuClick}
                className="nav-menu"
                style={{ flex: 'auto', minWidth: 0, borderBottom: 'none' }}
              />
            </div>
          </div>

          <div className="header-right">
            <div className="header-actions">
              {isAuthenticated ? (
                <Dropdown
                  menu={{
                    items: userMenuItems,
                    onClick: handleUserMenuClick,
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <button type="button" className="landing-user-menu-trigger">
                    <Avatar
                      size="default"
                      src={userData.profile || undefined}
                      className="user-avatar"
                    >
                      {!userData.profile && getUserInitials()}
                    </Avatar>
                    <span className="landing-user-name" title={userData.firstName || userData.email || 'Account'}>
                      {userData.firstName || userData.email || 'Account'}
                    </span>
                  </button>
                </Dropdown>
              ) : (
                <Button
                  type="text"
                  onClick={() => navigate('/login')}
                  className="sb-nav-login"
                >
                  Log In
                </Button>
              )}
            </div>

            <Button
              type="text"
              icon={<MenuOutlined />}
              className="mobile-menu-btn"
              onClick={() => setMobileMenuVisible(true)}
            />
          </div>
        </div>
      </Header>

      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        className="mobile-drawer"
      >
        <Menu
          mode="vertical"
          selectedKeys={[getActiveNavKey()]}
          items={navItems}
          onClick={handleMenuClick}
          className="mobile-nav-menu"
        />

        <div className="mobile-theme-toggle">
          <Button
            block
            type="text"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            className="mobile-theme-btn"
          >
            {isDark ? 'Light Mode' : 'Dark Mode'}
          </Button>
        </div>

        {isAuthenticated ? (
          <div className="mobile-auth-buttons">
            <Button
              block
              onClick={() => {
                navigate('/profile');
                setMobileMenuVisible(false);
              }}
              className="mobile-login-btn"
            >
              Profile
            </Button>
            <Button
              block
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="mobile-signup-btn"
            >
              Logout
            </Button>
          </div>
        ) : (
          <div className="mobile-auth-buttons">
            <Button
              block
              onClick={() => {
                navigate('/login');
                setMobileMenuVisible(false);
              }}
              className="mobile-login-btn"
            >
              Login
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                navigate('/signup-modern');
                setMobileMenuVisible(false);
              }}
              className="mobile-signup-btn"
            >
              Get Started
            </Button>
          </div>
        )}
      </Drawer>

      <Content className="landing-content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </Content>

      <Footer className="landing-footer" style={{ flexShrink: 0 }}>
        <div className="footer-container">
          <div className="footer-section" style={{ paddingRight: '2rem' }}>
            <div className="footer-logo">
              <BankOutlined className="logo-icon" />
              <span className="logo-text">SimplyFI</span>
            </div>
            <p className="footer-description">
              SEBI Registered Investment Advisor (INA0000XXXXX). SimplyFI is a platform providing analytics and intelligence for the fixed income market.
            </p>
          </div>

          <div className="footer-section">
            <h4>Platform</h4>
            <ul>
              <li><a href="/yield-curves">Yield Curves</a></li>
              <li><a href="/risk-analytics">Risk Analytics</a></li>
              <li><a href="/primary-market">Primary Market</a></li>
              <li><a href="/secondary-market">Secondary Market</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h4>Company</h4>
            <ul>
              <li><a href="/about">About Us</a></li>
              <li><a href="/methodology">Methodology</a></li>
              <li><a href="/terms">Terms of Service</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h4>Support</h4>
            <ul>
              <li><a href="/institutional">Institutional Desk</a></li>
              <li><a href="/api">API Documentation</a></li>
              <li><a href="/knowledge">Knowledge Base</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2024 SIMPLYFI INTELLIGENCE. ALL RIGHTS RESERVED.</p>
          <div className="footer-icons">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 11a9 9 0 0 1 9 9"></path>
              <path d="M4 4a16 16 0 0 1 16 16"></path>
              <circle cx="5" cy="19" r="1"></circle>
            </svg>
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default LandingLayoutRedesign;
